/* eslint-disable @typescript-eslint/no-explicit-any */
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { EventTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import { WTNScale } from '@clubspark/wtn-component-library';
import CloseIcon from '@mui/icons-material/Close';
import { Avatar, Drawer, IconButton, Link, List, ListItem, Tab } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Tabs from '@mui/material/Tabs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { competitionEngine } from 'tods-competition-factory';

import EventLink from 'components/events/EventLink';
import {
  RatingTypeEnum,
  ScaleTypeEnum,
} from 'components/panels/eventsSplitDraws/selectedEvent/rankingsAndRatings/types';
import TabPanel from 'components/tabs/TabPanel';
import { BodyRegular, FormLabelTextLarge, H2, H3 } from 'components/typography/Typography';
import { getClientConfig } from 'config/clients';
import ParticipantContactDetails from 'containers/participantContactDetails/ParticipantContactDetails';
import { usePersonContext } from 'hooks/person/usePersonContext';
import { useLevelConfig } from 'hooks/useLevelConfiguration';
import { type CloudError } from 'repository/CloudError';

import { useStyles } from './style';

interface PlayerProfileDialogProps {
  isOpen: boolean;
  participantId?: string;
  onClose: () => void;
}

const PLAYER_DIALOG_TABS = {
  details: {
    label: 'Details',
    value: 'details',
  },
  events: {
    label: 'Events',
    value: 'events',
  },
};

export const formatPhoneNumber = (phoneNumberString) => {
  const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }
  return phoneNumberString;
};

// eslint-disable-next-line complexity
const PlayerProfileDialog: React.FC<React.PropsWithChildren<PlayerProfileDialogProps>> = ({
  isOpen,
  participantId,
  onClose,
}) => {
  const [selectedTab, setSelectedTab] = useState(PLAYER_DIALOG_TABS.details.value);
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { getPlayerProfile } = usePersonContext() ?? {};
  const { wtnRankingsEnabled } = useLevelConfig();

  const { tournamentId, participant } = useMemo(
    () => competitionEngine.findParticipant({ participantId }),
    [participantId],
  );

  const person = useMemo(() => {
    const person = participant?.person;
    const age = dayjs().diff(person?.birthDate, 'year');
    const address = person?.addresses?.map((address) => `${address.city}, ${address.state}`);
    const [section, district] = ['ustaSection', 'ustaDistrict'].map(
      (name) => person?.extensions?.find((e) => e.name === name)?.value?.name,
    );
    return { ...(person ?? {}), age, address, section, district };
  }, [participant]);

  const [{ data, loading, error }, setGetPlayerProfileResult] = useState<{
    data?: any; // TODO: add type
    loading?: boolean;
    error?: CloudError;
  }>({ data: undefined });

  const personTournamentInfo = data?.person?.tournamentInfo;

  const wtnSingles = useMemo(() => {
    return competitionEngine.getParticipantScaleItem({
      participantId,
      scaleAttributes: {
        scaleType: ScaleTypeEnum.RATING,
        eventType: EventTypeEnum.SINGLES,
        scaleName: RatingTypeEnum.WTN,
      },
    })?.scaleItem?.scaleValue;
  }, [participantId]);

  const wtnDoubles = useMemo(() => {
    return competitionEngine.getParticipantScaleItem({
      participantId,
      scaleAttributes: {
        scaleType: ScaleTypeEnum.RATING,
        eventType: EventTypeEnum.DOUBLES,
        scaleName: RatingTypeEnum.WTN,
      },
    })?.scaleItem?.scaleValue;
  }, [participantId]);

  useEffect(() => {
    async function fetch() {
      if (person.personId && isOpen) {
        setGetPlayerProfileResult({ loading: true });
        const playerProfileResult = await getPlayerProfile(person.personId, tournamentId, 'no-cache');
        if (playerProfileResult?.error) {
          setGetPlayerProfileResult({ error: playerProfileResult.error, loading: false });
        } else {
          setGetPlayerProfileResult({ data: playerProfileResult.data, loading: false });
        }
      }
    }
    fetch();
  }, [person.personId, getPlayerProfile, tournamentId, isOpen]);

  const handleUpdateContactDetails = useCallback((contactData) => {
    setGetPlayerProfileResult((prev) => ({
      data: {
        ...prev.data,
        person: {
          ...prev.data.person,
          tournamentInfo: {
            ...prev.data.person.tournamentInfo,
            ...contactData,
          },
        },
      },
      loading: false,
    }));
  }, []);
  const showOrganisationId = getClientConfig().showOrganisationId;

  const handleTabChange = useCallback((_: unknown, val: string) => {
    setSelectedTab(val);
  }, []);

  return (
    <Drawer anchor="right" open={isOpen} sx={{ zIndex: 1300 }} onClose={onClose}>
      <DialogTitle className={classes.dialogTitle} id="player-profile-dialog-title">
        {error ? (
          <div className={classes.error}>{t('playerInfoError')}</div>
        ) : (
          <Grid container alignItems="center" justifyContent="flex-start">
            <Avatar
              className={classes.avatar}
            >{`${person?.standardGivenName[0]}${person?.standardFamilyName[0]}`}</Avatar>
            <Grid item>
              <H2>{`${person?.standardGivenName} ${person?.standardFamilyName}`}</H2>
              <Grid container justifyContent="flex-start">
                <Grid>
                  <BodyRegular component="span">{`${t(person.sex)}`}</BodyRegular>
                </Grid>
                <Grid>
                  <BodyRegular className={classes.separatorDot} component="span" />
                </Grid>
                <Grid>
                  <BodyRegular component="span">{`${person.age} ${t('years')}`}</BodyRegular>
                </Grid>
                {showOrganisationId && (
                  <>
                    <Grid item>
                      <BodyRegular className={classes.separatorDot} component="span" />
                    </Grid>
                    <Grid item>
                      <BodyRegular component="span">
                        {loading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <>
                            {t('uaid')}: {data?.person?.uaid ?? '?'}
                          </>
                        )}
                      </BodyRegular>
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent className={classes.dialogContent}>
        {wtnRankingsEnabled && (
          <div className={classes.wtnContainer}>
            <WTNScale
              infoIcon
              labelsDesktop
              animDuration={500}
              border={false}
              confidenceDoubles={wtnDoubles?.confidence ?? 0}
              confidenceSingles={wtnSingles?.confidence ?? 0}
              gameZoneLower={wtnSingles?.gameZoneLower ?? 0}
              gameZoneUpper={wtnSingles?.gameZoneUpper ?? 0}
              infoIconSize={25}
              infoModal={false}
              mobileBreakPoint={767.98}
              mode={'-'}
              width={360}
              wtnDoubles={wtnDoubles?.wtnRating}
              wtnSingles={wtnSingles?.wtnRating}
            />
          </div>
        )}
        {!error && (
          <>
            <Tabs className={classes.tabs} value={selectedTab} onChange={handleTabChange}>
              <Tab
                disableRipple
                component={Link}
                label={PLAYER_DIALOG_TABS.details.label}
                value={PLAYER_DIALOG_TABS.details.value}
              />
              <Tab
                disableRipple
                component={Link}
                label={PLAYER_DIALOG_TABS.events.label}
                value={PLAYER_DIALOG_TABS.events.value}
              />
            </Tabs>
            <Divider />
            <Grid className={classes.tabContainer}>
              <TabPanel index={PLAYER_DIALOG_TABS.details.value} value={selectedTab}>
                <Grid container spacing={2}>
                  <Grid item sm={6} xs={12}>
                    <FormLabelTextLarge>{`${t('section')} / ${t('district')}`}</FormLabelTextLarge>
                    <BodyRegular>{[person.section ?? t('unk'), person.district ?? t('unk')].join(' / ')}</BodyRegular>
                  </Grid>
                  <Grid item sm={6} xs={12}>
                    <FormLabelTextLarge>{t('city')}</FormLabelTextLarge>
                    <BodyRegular>
                      {person.address?.map((personAddress, ind) => (
                        <BodyRegular key={ind}>{personAddress}</BodyRegular>
                      ))}
                    </BodyRegular>
                  </Grid>
                  {personTournamentInfo && (
                    <Grid item xs={12}>
                      <ParticipantContactDetails
                        contactData={personTournamentInfo}
                        personId={person?.personId}
                        tournamentId={tournamentId}
                        updateContactDetails={handleUpdateContactDetails}
                      />
                    </Grid>
                  )}
                </Grid>
              </TabPanel>
              <TabPanel index={PLAYER_DIALOG_TABS.events.value} value={selectedTab}>
                <Grid container>
                  <Grid item xs={12}>
                    <H3>{t('eventsInThisTournament')}:</H3>
                  </Grid>
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <List dense className={classes.listItemCont}>
                      {personTournamentInfo?.registeredEvents?.map((event) => {
                        return (
                          <ListItem key={event.eventId} className={classes.listItem}>
                            <EventLink
                              eventId={event?.eventId}
                              eventName={event?.eventName}
                              tournamentId={tournamentId}
                              onClick={onClose}
                            >
                              {event?.eventName}
                            </EventLink>
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </Grid>
              </TabPanel>
            </Grid>
          </>
        )}
      </DialogContent>
    </Drawer>
  );
};

export default PlayerProfileDialog;
